// useScreenshotDeter — blackout-hook for sensitive views.
//
// Web/PWA har INGEN pålitelig screenshot-detektor (Android og iOS Safari
// eksponerer ikke slike events). Denne hooken gir best-effort mottiltak:
//
// 1. `visibilitychange` (page hidden) → øyeblikkelig blackout.
//    Utløses når brukeren bytter fane, minimerer, eller Android's
//    screenshot-overlay animerer bort fra vår side.
// 2. `blur` (window/tab mister fokus) → blackout.
//    Fanger opp Alt+Tab, PrintScreen-verktøy som stjeler fokus.
// 3. `keydown` PrintScreen → blackout + logger.
// 4. `contextmenu` (høyreklikk) → forhindret.
// 5. Rapporterer forsøk til backend (fire-and-forget) via
//    `/api/security/screenshot-attempt` når endepunktet finnes.
//
// Bruk:
//   const { blackout, ref } = useScreenshotDeter({ label: "gallery" });
//   return <div ref={ref}>{blackout ? <Curtain/> : <SensitiveContent/>}</div>
//
// Blackout løses opp automatisk 400 ms etter at vinduet er fokusert igjen.
import { useEffect, useRef, useState, useCallback } from "react";

const RESUME_DELAY_MS = 400;

export default function useScreenshotDeter({ label = "unknown", enabled = true, onAttempt } = {}) {
  const [blackout, setBlackout] = useState(false);
  const elRef = useRef(null);
  const resumeTimer = useRef(null);

  const trigger = useCallback((reason) => {
    setBlackout(true);
    onAttempt?.(reason);
    // Fire-and-forget log — feiler stille hvis endepunkt ikke finnes
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/security/screenshot-attempt`;
      const body = JSON.stringify({ label, reason, at: Date.now() });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      } else {
        fetch(url, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [label, onAttempt]);

  const clearBlackout = useCallback(() => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setBlackout(false), RESUME_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onVis = () => {
      if (document.visibilityState === "hidden") trigger("visibility_hidden");
      else clearBlackout();
    };
    const onBlur = () => trigger("window_blur");
    const onFocus = () => clearBlackout();
    const onKey = (e) => {
      if (e.key === "PrintScreen" || (e.metaKey && e.shiftKey && (e.key === "3" || e.key === "4" || e.key === "5"))) {
        trigger("print_screen_key");
      }
    };
    const onCtx = (e) => {
      if (elRef.current && elRef.current.contains(e.target)) {
        e.preventDefault();
        trigger("context_menu");
      }
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("keydown", onKey);
    document.addEventListener("contextmenu", onCtx);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("contextmenu", onCtx);
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };
  }, [enabled, trigger, clearBlackout]);

  return { blackout, ref: elRef, trigger };
}
